import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, MessageSquare, FileText, User, Search, SquarePen } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useMemo } from 'react';
import PositListCard from '@/components/posit/PositListCard';

const UserProfile = () => {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [pricingFilter, setPricingFilter] = useState("all"); // all, free, paid

  // Fetch user data by handle
  const { data: profileUser, isLoading: isLoadingUser, error: profileError } = useQuery({
    queryKey: ['user-profile', handle],
    queryFn: async () => {
      if (!handle) throw new Error('No handle provided');

      // Validate handle format before making request
      const handleRegex = /^[a-zA-Z0-9_-]+$/;
      if (!handleRegex.test(handle)) {
        throw new Error('Invalid handle format');
      }

      const { data, error } = await supabase
        .from('users')
        .select('id, first_name, last_name, handle, created_at, bio')
        .eq('handle', handle)
        .maybeSingle(); // Use maybeSingle instead of single to avoid 406 errors

      if (error) {
        throw error;
      }
      
      if (!data) {
        throw new Error('User not found');
      }
      
      return data;
    },
    enabled: !!handle,
    retry: false, // Don't retry on 404/406 errors
  });

  // Fetch user's public posits with chat counts
  const { data: posits, isLoading: isLoadingPosits } = useQuery({
    queryKey: ['user-posits', profileUser?.id],
    queryFn: async () => {
      if (!profileUser?.id) return [];

      const { data, error } = await supabase
        .from('posits')
        .select(`
          id,
          title,
          summary_light,
          posit_description,
          public_description,
          privacy,
          is_monetized,
          subscription_price,
          created_at,
          updated_at
        `)
        .eq('user_id', profileUser.id)
        .in('privacy', ['public', 'exclusive'])
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!profileUser?.id,
  });

  // Fetch total chat stats across all posits
  const { data: chatStats } = useQuery({
    queryKey: ['user-chat-stats', profileUser?.id],
    queryFn: async () => {
      if (!profileUser?.id || !posits || posits.length === 0) return { totalChats: 0 };

      const positIds = posits.map(p => p.id);
      
      // Get total chat count
      const { count, error } = await supabase
        .from('chat_history')
        .select('*', { count: 'exact', head: true })
        .in('posit_id', positIds);

      if (error) {
        return { totalChats: 0 };
      }

      return { totalChats: count || 0 };
    },
    enabled: !!profileUser?.id && !!posits && posits.length > 0,
  });

  const isOwnProfile = user?.id === profileUser?.id;

  // Filter posits based on search and pricing
  const filteredPosits = useMemo(() => {
    if (!posits) return [];

    return posits.filter(posit => {
      // Search filter
      const matchesSearch = !searchQuery || 
        posit.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (posit.posit_description && posit.posit_description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (posit.summary_light && posit.summary_light.toLowerCase().includes(searchQuery.toLowerCase()));

      // Pricing filter
      const isPaid = posit.is_monetized && posit.subscription_price && posit.subscription_price > 0;
      const matchesPricing = pricingFilter === "all" || 
        (pricingFilter === "free" && !isPaid) ||
        (pricingFilter === "paid" && isPaid);

      return matchesSearch && matchesPricing;
    });
  }, [posits, searchQuery, pricingFilter]);

  // Clear search function
  const clearSearch = () => {
    setSearchQuery("");
    setPricingFilter("all");
  };

  if (isLoadingUser) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (profileError || !profileUser) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-semibold mb-2">User not found</h2>
          <p className="text-muted-foreground mb-4">
            {handle ? `No user found with handle "@${handle}"` : 'The profile you\'re looking for doesn\'t exist.'}
          </p>
          <div className="space-y-2">
            <Button onClick={() => navigate('/public-posits')}>Browse Public Posits</Button>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const displayName = `${profileUser.first_name || ''} ${profileUser.last_name || ''}`.trim() || profileUser.handle;

  return (
    <DashboardLayout
      description="View profile and public knowledge bases"
      maxWidth="2xl"
    >
      {/* Profile Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">{displayName}</h1>
            {profileUser.bio && (
              <p className="text-muted-foreground max-w-2xl">{profileUser.bio}</p>
            )}
          </div>
          {/* Future: Add follow button here */}
          {!isOwnProfile && (
            <div className="flex gap-2">
              {/* <Button variant="outline">Follow</Button> */}
            </div>
          )}
        </div>
      </div>

      {/* Posits Section */}
      <div>
        {/* Search and Filter Controls */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search posits..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Pricing Filter */}
            <Select value={pricingFilter} onValueChange={setPricingFilter}>
              <SelectTrigger className="w-full sm:w-[140px]">
                <SelectValue placeholder="Pricing" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Posits</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Clear Filters */}
            {(searchQuery || pricingFilter !== "all") && (
              <Button variant="outline" onClick={clearSearch} className="whitespace-nowrap">
                Clear Filters
              </Button>
            )}
          </div>
        </div>
        
        {isLoadingPosits ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : filteredPosits && filteredPosits.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredPosits.map((posit) => {
              const isPaid = posit.is_monetized && posit.subscription_price > 0;
              const price = isPaid ? `$${posit.subscription_price}` : null;
              return (
                <PositListCard
                  key={posit.id}
                  posit={posit}
                  showEditButton={false}
                  showCreatorButton={false}
                  showShareButton={false}
                  showWikiButton={false}
                  variant="compact"
                />
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="text-center py-12">
              {searchQuery || pricingFilter !== "all" ? (
                <div>
                  <p className="text-muted-foreground mb-4">
                    No posits found matching your search criteria
                  </p>
                  <Button variant="outline" onClick={clearSearch}>
                    Clear Filters
                  </Button>
                </div>
              ) : (
                <p className="text-muted-foreground">No public Posits yet</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default UserProfile; 